import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Flex,
  VStack,
  useToast,
  Spinner,
  Text,
  FormControl,
  FormLabel,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react';
import { useQuery, useMutation } from 'react-query';
import {
  getHourlyReportSettings,
  getDailyReportSettings,
  postHourlyReportSetting,
  postDailyReportSetting,
  deleteHourlyReportSetting,
  deleteDailyReportSetting,
  setDailyReportHour,
} from '../api/reportSettingsApi';
import { GetAllTags } from '../api/tag';

const NAVBAR_HEIGHT = 50;

const SettingsPage = () => {
  const toast = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [deviceTagMap, setDeviceTagMap] = useState([]);
  const [modalType, setModalType] = useState(''); // 'hourly' or 'daily'
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState('');
  const [deleteItemType, setDeleteItemType] = useState('');
  const [hourModalOpen, setHourModalOpen] = useState(false);
  const [newHour, setNewHour] = useState(0);

  const {
    data: hourlyData,
    error: hourlyError,
    isLoading: hourlyLoading,
    refetch: refetchHourly,
  } = useQuery('hourlyReportSettings', getHourlyReportSettings, {
    refetchOnWindowFocus: false,
  });

  const {
    data: dailyData,
    error: dailyError,
    isLoading: dailyLoading,
    refetch: refetchDaily,
  } = useQuery('dailyReportSettings', getDailyReportSettings, {
    refetchOnWindowFocus: false,
  });

  const {
    data: tagsData,
    error: tagsError,
    isLoading: tagsLoading,
  } = useQuery('tags', GetAllTags, {
    onSuccess: data => {
      setDeviceTagMap(data);
    },
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation(
    modalType === 'hourly' ? postHourlyReportSetting : postDailyReportSetting,
    {
      onSuccess: () => {
        toast({
          title: `${modalType === 'hourly' ? 'Hourly' : 'Daily'} report setting added.`,
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
        if (modalType === 'hourly') {
          refetchHourly();
        } else {
          refetchDaily();
        }
        setIsModalOpen(false);
      },
      onError: error => {
        toast({
          title: 'Error adding setting.',
          description: error.response?.data?.message || error.message,
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
      },
    }
  );

  const deleteMutation = useMutation(
    deleteItemType === 'hourly'
      ? deleteHourlyReportSetting
      : deleteDailyReportSetting,
    {
      onSuccess: () => {
        toast({
          title: `${deleteItemType === 'hourly' ? 'Hourly' : 'Daily'} report setting deleted.`,
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
        if (deleteItemType === 'hourly') {
          refetchHourly();
        } else {
          refetchDaily();
        }
        setDeleteModalOpen(false);
      },
      onError: error => {
        toast({
          title: 'Error deleting setting.',
          description: error.response?.data?.message || error.message,
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
      },
    }
  );

  useEffect(() => {
    if (dailyData?.hour != null) {
      setNewHour(dailyData.hour);
    }
  }, [dailyData]);

  const handleSave = () => {
    if (selectedDevice && selectedTag) {
      mutation.mutate({
        device_id: selectedDevice,
        tag_id: selectedTag,
        enabled: true,
      });
    }
  };

  const handleSaveHour = async () => {
    try {
      await setDailyReportHour(parseInt(newHour, 10));
      toast({
        title: 'Daily report hour updated.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      refetchDaily();
      setHourModalOpen(false);
    } catch (error) {
      toast({
        title: 'Error updating hour.',
        description: error.message,
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleAddHourly = () => {
    setModalType('hourly');
    setSelectedDevice('');
    setSelectedTag('');
    setIsModalOpen(true);
  };

  const handleAddDaily = () => {
    setModalType('daily');
    setSelectedDevice('');
    setSelectedTag('');
    setIsModalOpen(true);
  };

  const handleDelete = (id, type) => {
    setDeleteItemId(id);
    setDeleteItemType(type);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    deleteMutation.mutate(deleteItemId);
  };

  if (hourlyLoading || dailyLoading || tagsLoading) {
    return (
      <Box
        w="100%"
        h="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    );
  }

  if (hourlyError || dailyError || tagsError) {
    return (
      <Box w="100%" display="flex" justifyContent="center" p={5}>
        <Text>
          An error occurred:{' '}
          {hourlyError?.message || dailyError?.message || tagsError?.message}
        </Text>
      </Box>
    );
  }

  const selectedDeviceTags =
    deviceTagMap.find(device => device.id === selectedDevice)?.tags || [];

  return (
    <Flex
      direction="column"
      w="full"
      p={4}
      h={`calc(90,5vh - ${NAVBAR_HEIGHT}px)`}
      mt={`${NAVBAR_HEIGHT}px`}
      overflow="hidden"
    >
      <VStack spacing={4} align="stretch" h="full">
        <Box flex="1" overflow="hidden">
          <Heading as="h4" size="md" mb={1}>
            Registers for hourly report
          </Heading>
          <Box
            border="2px solid"
            borderColor="gray.200"
            rounded="md"
            h="300px" // Set a fixed height to enable inner scrolling
            overflowY="auto"
          >
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Device</Th>
                    <Th>Tag</Th>
                    <Th>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {hourlyData?.map(item => (
                    <Tr key={item.id}>
                      <Td>{item.device}</Td>
                      <Td>{item.tag}</Td>
                      <Td>
                        <Button
                          colorScheme="red"
                          size="xs"
                          onClick={() => handleDelete(item.id, 'hourly')}
                        >
                          Delete
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
          <Flex justify="flex-end" mt={2}>
            <Button colorScheme="blue" size="sm" onClick={handleAddHourly}>
              Add
            </Button>
          </Flex>
        </Box>

        <Box flex="1" overflow="hidden">
          <Heading as="h2" size="md" mb={2}>
            Registers for daily report
          </Heading>
          <Box
            border="1px solid"
            borderColor="gray.200"
            rounded="md"
            h="300px" // Set a fixed height to enable inner scrolling
            overflowY="auto"
          >
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Device</Th>
                    <Th>Tag</Th>
                    <Th>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {dailyData?.dailyList?.map(item => (
                    <Tr key={item.id}>
                      <Td>{item.device}</Td>
                      <Td>{item.tag}</Td>
                      <Td>
                        <Button
                          colorScheme="red"
                          size="xs"
                          onClick={() => handleDelete(item.id, 'daily')}
                        >
                          Delete
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
          <Flex justify="flex-end" mt={2}>
            <Button
              colorScheme="teal"
              size="sm"
              onClick={() => setHourModalOpen(true)}
              mr={2}
            >
              Set Hour ({dailyData?.hour}:00)
            </Button>
            <Button colorScheme="blue" size="sm" onClick={handleAddDaily}>
              Add
            </Button>
          </Flex>
        </Box>
      </VStack>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{`Add ${modalType.charAt(0).toUpperCase() + modalType.slice(1)} Report Setting`}</ModalHeader>
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Device</FormLabel>
              <Select
                placeholder="Select device"
                value={selectedDevice}
                onChange={e => setSelectedDevice(e.target.value)}
              >
                {deviceTagMap.map(device => (
                  <option key={device.id} value={device.id}>
                    {device.deviceName}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Tag</FormLabel>
              <Select
                placeholder="Select tag"
                value={selectedTag}
                onChange={e => setSelectedTag(e.target.value)}
                isDisabled={!selectedDevice}
              >
                {selectedDeviceTags.map(tag => (
                  <option key={tag.registerId} value={tag.registerId}>
                    {tag.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSave} ml={3}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Report Setting</ModalHeader>
          <ModalBody>
            <Text>
              Delete the report settings? This process cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={confirmDelete} ml={3}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={hourModalOpen} onClose={() => setHourModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Set Daily Report Hour</ModalHeader>
          <ModalBody>
            <FormControl>
              <FormLabel>Select Hour (0-23)</FormLabel>
              <Select
                value={newHour}
                onChange={e => setNewHour(e.target.value)}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>
                    {i}:00
                  </option>
                ))}
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setHourModalOpen(false)}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveHour} ml={3}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default SettingsPage;
